import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import ProductCard from '../components/products/ProductCard';
import SEO from '../components/SEO';
import { ChevronRight, ChevronLeft } from 'lucide-react';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const brandImages = [
    require('../assets/brands/brand1.jpg'),
    require('../assets/brands/brandtest.png')
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % brandImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + brandImages.length) % brandImages.length);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % brandImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [brandImages.length]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const q = query(
        collection(db, 'products'),
        where('featured', '==', true),
        limit(8)
      );
      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFeaturedProducts(products);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Kính mắt Nhân Văn - Kính Mát & Kính Cận Cao Cấp"
        description="Mua kính mát và kính cận cao cấp tại Kính mắt Nhân Văn. Miễn phí vận chuyển, thanh toán khi nhận hàng, đổi trả trong 7 ngày. Khám phá kính thời trang với bảo vệ UV400 và lọc ánh sáng xanh."
        keywords="kính mát, kính cận, kính mắt, kính online, bảo vệ UV, kính chống ánh sáng xanh, kính aviator, kính mắt mèo, kính thời trang"
        ogType="website"
      />
      <div className="min-h-screen">
        {/* Brands Carousel Section */}
        <section className="relative w-full h-64 md:h-96 overflow-hidden bg-gray-100">
          {/* Carousel Images */}
          {brandImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 flex items-center justify-center ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt={`Brand ${index + 1}`}
                className="w-full h-full object-contain"
              />
            </div>
          ))}

          {/* Previous Button */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition z-10"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition z-10"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 text-gray-800" />
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {brandImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition ${
                  index === currentSlide ? 'bg-white' : 'bg-white/50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </section>

        {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-4xl font-bold mb-6">
              Nhìn thế giới qua lăng kính của bạn
            </h1>
            <p className="text-xl md:text-1.5xl mb-8">
              Khám phá bộ sưu tập kính mát và kính cận cao cấp của chúng tôi
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/products?category=sunglasses"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Mua Kính Mát
              </Link>
              <Link
                to="/products?category=nearsighted"
                className="border-2 border-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition"
              >
                Mua Kính Cận
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Mua Theo Danh Mục</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Link
              to="/products?category=sunglasses"
              className="group relative overflow-hidden rounded-xl shadow-lg h-64"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
              <img
                src="https://images.unsplash.com/photo-1511499767150-a48a237f0083?w=600"
                alt="Sunglasses"
                className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                <h3 className="text-white text-2xl font-bold mb-2">Kính Mát</h3>
                <p className="text-white/90 flex items-center gap-2">
                  Khám Phá Bộ Sưu Tập <ChevronRight className="w-5 h-5" />
                </p>
              </div>
            </Link>

            <Link
              to="/products?category=nearsighted"
              className="group relative overflow-hidden rounded-xl shadow-lg h-64"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
              <img
                src="https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=600"
                alt="Kính Cận"
                className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                <h3 className="text-white text-2xl font-bold mb-2">Kính Cận</h3>
                <p className="text-white/90 flex items-center gap-2">
                  Khám Phá Bộ Sưu Tập <ChevronRight className="w-5 h-5" />
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold">Sản Phẩm Nổi Bật</h2>
            <Link
              to="/products"
              className="text-primary-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all"
            >
              Xem Tất Cả <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Chưa có sản phẩm nổi bật nào.
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Cam Kết Chất Lượng</h3>
              <p className="text-gray-600">Tất cả sản phẩm 100% chính hãng và được kiểm tra chất lượng</p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Thanh Toán Khi Nhận Hàng</h3>
              <p className="text-gray-600">Thanh toán bằng tiền mặt khi nhận hàng tại nhà</p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Đổi Trả Trong 7 Ngày</h3>
              <p className="text-gray-600">Không hài lòng? Đổi trả trong vòng 7 ngày, không cần lý do</p>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Home;
